import React, { useMemo } from 'react';
import { Tooltip, Spin, Button, theme } from 'antd';
import { DollarOutlined, EuroOutlined, SyncOutlined } from '@ant-design/icons';
import { useGetExchangeRatesQuery } from '../../api/exchangeRateApi';
import { useSelector } from 'react-redux';
import { selectUserType } from '../../auth/slices/authSlice';

interface ExchangeRateHeaderWidgetProps {
  isMobile?: boolean;
  onSync?: () => void;
}

const { useToken } = theme;

export const ExchangeRateHeaderWidget: React.FC<ExchangeRateHeaderWidgetProps> = ({
  isMobile = false,
  onSync,
}) => {
  const { token } = useToken();
  const userType = useSelector(selectUserType);

  const { data: exchangeRates, isLoading, isFetching, refetch } = useGetExchangeRatesQuery();

  const handleSync = () => {
    if (onSync) {
      onSync();
    }
    refetch();
  };

  const { usdRate, eurRate, latestDate } = useMemo(() => {
    if (!exchangeRates) return { usdRate: null, eurRate: null, latestDate: null };

    const usd = exchangeRates.find((r) => r.currencyCode === 'USD');
    const eur = exchangeRates.find((r) => r.currencyCode === 'EUR');

    const dateStr = usd?.createdAt || eur?.createdAt;
    const date = dateStr ? new Date(dateStr) : null;

    return { usdRate: usd, eurRate: eur, latestDate: date };
  }, [exchangeRates]);

  if (isLoading) {
    return <Spin size="small" style={{ margin: '0 8px' }} />;
  }

  if (!usdRate && !eurRate) return null;

  const isAdmin = userType === 1;

  const formattedDate = latestDate
    ? latestDate.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Desconocida';

  const RateDisplay = ({ rate, label }: { rate: { rateValue: number }; label: string }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, lineHeight: 1 }}>
      {!isMobile && (
        <span style={{ color: token.colorTextSecondary, fontSize: 12, fontWeight: 500 }}>
          {label}
        </span>
      )}
      <span
        style={{
          color: token.colorTextBase,
          fontSize: isMobile ? 12 : 13,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
        }}
      >
        {rate.rateValue.toFixed(2)}
      </span>
      <span style={{ color: token.colorTextTertiary, fontSize: 10, fontWeight: 600 }}>VES</span>
    </div>
  );

  return (
    <Tooltip
      placement="bottom"
      title={
        <div style={{ padding: '2px 4px' }}>
          <div style={{ fontWeight: 600 }}>Tasa Oficial de Cambio (BCV)</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>
            {usdRate && <div>1 USD = {usdRate.rateValue.toFixed(2)} VES</div>}
            {eurRate && <div>1 EUR = {eurRate.rateValue.toFixed(2)} VES</div>}
          </div>
          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
            Actualizado: {formattedDate}
          </div>
          <div style={{ fontSize: 10, color: token.colorPrimary, marginTop: 6 }}>
            {isAdmin ? 'Haz clic para sincronizar con BCV' : 'Haz clic para actualizar los datos'}
          </div>
        </div>
      }
    >
      <Button
        type="text"
        onClick={handleSync}
        loading={isFetching && !usdRate && !eurRate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: isMobile ? 32 : 36,
          padding: isMobile ? '0 8px' : '0 12px',
          borderRadius: 20,
          backgroundColor: token.colorFillTertiary,
          border: `1px solid ${token.colorBorderSecondary}`,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
        }}
        className="exchange-rate-widget"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: token.colorSuccess,
              boxShadow: `0 0 6px ${token.colorSuccess}`,
              flexShrink: 0,
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {usdRate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <DollarOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
              <RateDisplay rate={usdRate} label="1 USD =" />
            </div>
          )}

          {usdRate && eurRate && (
            <div style={{ width: 1, height: 12, backgroundColor: token.colorBorder }} />
          )}

          {eurRate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EuroOutlined style={{ color: '#1677ff', fontSize: 12 }} />
              <RateDisplay rate={eurRate} label="1 EUR =" />
            </div>
          )}
        </div>

        {isFetching && (
          <SyncOutlined spin style={{ fontSize: 10, color: token.colorPrimary, marginLeft: 2 }} />
        )}
      </Button>
    </Tooltip>
  );
};

export default ExchangeRateHeaderWidget;
